import React from 'react';
import { FiX, FiSettings, FiMapPin, FiTruck, FiCalendar, FiClock, FiTrendingUp, FiDroplet, FiCreditCard, FiStar } from 'react-icons/fi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChaloAIIcon } from '@/components/ui/ChaloAIIcon';

interface PricingExplanationModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestedPrice: number;
  minPrice: number;
  maxPrice: number;
  distance: number;
  vehicleType: string;
}

export const PricingExplanationModal: React.FC<PricingExplanationModalProps> = ({
  isOpen,
  onClose,
  suggestedPrice,
  minPrice,
  maxPrice,
  distance,
  vehicleType
}) => {
  if (!isOpen) return null;

  const factors = [
    { icon: FiMapPin, label: 'Distance', value: `${distance} km`, impact: 'Base calculation' },
    { icon: FiTruck, label: 'Vehicle Type', value: vehicleType, impact: 'Premium adjustment' },
    { icon: FiCalendar, label: 'Holiday/Weekend', value: 'Weekend', impact: '+10% demand surge' },
    { icon: FiClock, label: 'Time of Travel', value: 'Peak hours', impact: '+15% surge pricing' },
    { icon: FiTrendingUp, label: 'Demand', value: 'High', impact: 'Market-based pricing' },
    { icon: FiDroplet, label: 'Fuel Charges', value: 'Current rates', impact: 'Real-time adjustment' },
    { icon: FiCreditCard, label: 'Toll Charges', value: 'Route-based', impact: 'Added to base price' },
    { icon: FiStar, label: 'Preferences', value: 'AC, Music', impact: 'Additional charges' },
    { icon: FiSettings, label: 'Calculation', value: 'Dynamic', impact: 'AI-powered' },
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ChaloAIIcon size={24} />
            <h3 className="text-lg font-semibold text-gray-900">AI Pricing Calculation</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
            <FiX className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Price Summary */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="text-center">
                <h4 className="font-semibold text-blue-900 mb-2">Suggested Price Range</h4>
                <div className="flex items-center justify-center space-x-4">
                  <span className="text-sm text-blue-700">Min: ₹{minPrice}</span>
                  <span className="text-xl font-bold text-blue-900">₹{suggestedPrice}</span>
                  <span className="text-sm text-blue-700">Max: ₹{maxPrice}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Factors */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800">Factors Considered:</h4>
            {factors.map((factor, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                  <factor.icon className="w-4 h-4 text-gray-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{factor.label}</span>
                    <span className="text-sm text-gray-600">{factor.value}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{factor.impact}</p>
                </div>
              </div>
            ))}
          </div>

          {/* AI Note */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-700">
                <strong>Chalo AI Note:</strong> Our algorithm considers real-time data and market conditions to suggest fair pricing. 
                You can adjust within the recommended range for better passenger attraction while ensuring good earnings.
              </p>
            </CardContent>
          </Card>

          <Button onClick={onClose} className="w-full">
            Got it, thanks!
          </Button>
        </div>
      </div>
    </div>
  );
};
